import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  updateDoc,
  collection,
  doc,
} from "firebase/firestore";
import Swal from "sweetalert2";
import NavBar from "./NavBar";
import { auth, db } from "../config";

function Users() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [kycModalUser, setKycModalUser] = useState(null);
  const [lastVisible, setLastVisible] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [updateAmounts, setUpdateAmounts] = useState({
    balance: 0,
    Totaldeposit: 0,
    Totalwithdrawal: 0,
    totalearnings: 0,
    TotalBonus: 0,
    withdrawalLimit: 0,
    withdrawalFeePercentage: 0,
  });

  const itemsPerPage = 100;
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredUsers(users);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = users.filter((user) =>
        (user.fullname || "").toLowerCase().includes(term)
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    if (!hasMore || loading) return;
    setLoading(true);

    try {
      let q = query(
        collection(db, "users"),
        orderBy("timestamp", "desc"),
        limit(itemsPerPage)
      );

      if (lastVisible) {
        q = query(
          collection(db, "users"),
          orderBy("timestamp", "desc"),
          startAfter(lastVisible),
          limit(itemsPerPage)
        );
      }

      const snapshot = await getDocs(q);
      const fetchedUsers = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      setUsers((prev) => [...prev, ...fetchedUsers]);
      setLastVisible(snapshot.docs[snapshot.docs.length - 1] || null);
      if (fetchedUsers.length < itemsPerPage) setHasMore(false);
    } catch (error) {
      Swal.fire("Error", "Failed to fetch users.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setUpdateAmounts({
      balance: 0,
      Totaldeposit: 0,
      Totalwithdrawal: 0,
      totalearnings: 0,
      TotalBonus: 0,
      withdrawalLimit: 0,
      withdrawalFeePercentage: 0,
    });
  };

  const handleFieldUpdate = async (field, amount) => {
    if (!selectedUser) return;

    try {
      const currentValue = selectedUser[field] || 0;
      const newValue = currentValue + amount;
      const userRef = doc(db, "users", selectedUser.id);

      await updateDoc(userRef, {
        [field]: newValue,
      });

      Swal.fire("Success", `${field} updated to ${newValue}`, "success");

      setUsers((prev) =>
        prev.map((user) =>
          user.id === selectedUser.id ? { ...user, [field]: newValue } : user
        )
      );
      setSelectedUser((prev) => ({ ...prev, [field]: newValue }));
    } catch (error) {
      Swal.fire("Error", `Failed to update ${field}.`, "error");
    }
  };

  const handleToggleVerification = async (user) => {
    try {
      const newVerifiedStatus = !user.verified;
      const userRef = doc(db, "users", user.id);

      await updateDoc(userRef, {
        verified: newVerifiedStatus,
      });

      Swal.fire(
        "Success",
        `User ${newVerifiedStatus ? "verified" : "unverified"} successfully!`,
        "success"
      );

      setUsers((prev) =>
        prev.map((u) =>
          u.id === user.id ? { ...u, verified: newVerifiedStatus } : u
        )
      );
    } catch (error) {
      Swal.fire("Error", "Failed to update verification status.", "error");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? dateStr : date.toDateString();
  };

  const handleDeleteUser = async (userId) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (confirm.isConfirmed) {
      try {
        // Call your backend delete endpoint
        const res = await fetch(
          `https://renderbackend-24k2.onrender.com/api/deleteUser/${userId}`,
          {
            method: "DELETE",
          }
        );

        if (!res.ok) {
          const message = await res.text();
          throw new Error(message || "Failed to delete user");
        }

        // Update UI
        setUsers((prev) => prev.filter((u) => u.id !== userId));
        setFilteredUsers((prev) => prev.filter((u) => u.id !== userId));
        Swal.fire("Deleted!", "User has been deleted.", "success");
      } catch (error) {
        console.error("Delete user error:", error);
        Swal.fire("Error", error.message || "Failed to delete user.", "error");
      }
    }
  };

  return (
    <div className="container my-5">
      <nav className="main-header navbar navbar-expand navbar-white navbar-light">
        <ul className="navbar-nav">
          <li className="nav-item">
            <a
              className="nav-link"
              data-widget="pushmenu"
              href="#"
              role="button"
            >
              <i className="fas fa-bars" />
            </a>
          </li>
        </ul>
      </nav>
      <NavBar />
      <h2 className="text-center mb-4">Manage Users</h2>

      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search by name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="table-responsive">
        <table className="table table-bordered table-hover">
          <thead className="thead-dark">
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Password</th>
              <th>Balance</th>
              <th>Total Deposit</th>
              <th>Total Withdrawal</th>
              <th>Total Earnings</th>
              <th>Total Bonus</th>
              <th>Verified</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id}>
                <td>{user.fullname || "N/A"}</td>
                <td>{user.email || "N/A"}</td>
                <td>{user.password || "N/A"}</td>
                <td>{user.balance || 0}</td>
                <td>{user.Totaldeposit || 0}</td>
                <td>{user.Totalwithdrawal || 0}</td>
                <td>{user.totalearnings || 0}</td>
                <td>{user.TotalBonus || 0}</td>
                <td>{user.verified ? "Yes" : "No"}</td>
                <td>
                  <button
                    className="btn btn-primary btn-sm mr-2"
                    onClick={() => handleSelectUser(user)}
                  >
                    Manage
                  </button>
                  <button
                    className="btn btn-info btn-sm mr-2"
                    onClick={() => setKycModalUser(user)}
                  >
                    View KYC
                  </button>
                  <button
                    className="btn btn-warning btn-sm"
                    onClick={() => handleToggleVerification(user)}
                  >
                    {user.verified ? "Unverify" : "Verify"}
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDeleteUser(user.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div className="text-center">
          <button
            className="btn btn-info"
            onClick={fetchUsers}
            disabled={loading}
          >
            {loading ? "Loading..." : "Load More"}
          </button>
        </div>
      )}

      {/* Manage User Modal */}
      {selectedUser && (
        <div className="modal custom-modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Manage User</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setSelectedUser(null)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <h6>Update Fields:</h6>
                {Object.keys(updateAmounts).map((field) => (
                  <div className="form-group" key={field}>
                    <label>{field}</label>
                    <div className="input-group">
                      <input
                        type="number"
                        className="form-control"
                        value={updateAmounts[field] || ""}
                        onChange={(e) =>
                          setUpdateAmounts((prev) => ({
                            ...prev,
                            [field]: Number(e.target.value),
                          }))
                        }
                      />
                      <div className="input-group-append">
                        <button
                          className="btn btn-success"
                          onClick={() =>
                            handleFieldUpdate(field, updateAmounts[field])
                          }
                        >
                          Add
                        </button>
                        <button
                          className="btn btn-danger"
                          onClick={() =>
                            handleFieldUpdate(field, -updateAmounts[field])
                          }
                        >
                          Subtract
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setSelectedUser(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KYC Modal */}
      {kycModalUser?.kyc && (
        <div className="modal custom-modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">KYC Details</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setKycModalUser(null)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p>
                  <strong>Full Name:</strong>{" "}
                  {kycModalUser.kyc.fullName || "N/A"}
                </p>
                <p>
                  <strong>DOB:</strong> {formatDate(kycModalUser.kyc.dob)}
                </p>
                <p>
                  <strong>ID Type:</strong> {kycModalUser.kyc.idType}
                </p>
                <p>
                  <strong>ID Number:</strong> {kycModalUser.kyc.idNumber}
                </p>
                <p>
                  <strong>Country:</strong> {kycModalUser.kyc.country}
                </p>
                <p>
                  <strong>Address:</strong> {kycModalUser.kyc.address}
                </p>
                <p>
                  <strong>Expiry Date:</strong>{" "}
                  {formatDate(kycModalUser.kyc.expiryDate)}
                </p>
                <p>
                  <strong>Status:</strong> {kycModalUser.kyc.status}
                </p>

                <div className="row">
                  <div className="col-md-4">
                    <img
                      src={kycModalUser.kyc.idFrontURL}
                      alt="ID Front"
                      className="img-fluid"
                    />
                    <p className="text-center">ID Front</p>
                  </div>
                  <div className="col-md-4">
                    <img
                      src={kycModalUser.kyc.idBackURL}
                      alt="ID Back"
                      className="img-fluid"
                    />
                    <p className="text-center">ID Back</p>
                  </div>
                  <div className="col-md-4">
                    <img
                      src={kycModalUser.kyc.selfieURL}
                      alt="Selfie"
                      className="img-fluid"
                    />
                    <p className="text-center">Selfie</p>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setKycModalUser(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
